import { useEffect, useRef, useState } from 'react';
import { useOrchestratorContext } from './useOrchestratorContext';
import { getSession, AttachedDocument } from '../services/smartStartApi';

export interface SmartRouterSessionContext {
  inRouter: boolean;
  sessionId: number | null;
  loading: boolean;
  userGoal: string | null;
  attachedDocuments: AttachedDocument[];
  intakeSlots: Record<string, string>;
  pipelineName: string | null;
  stepLabel: string | null;
  stepNumber: number | null;
  stepTotal: number | null;
}

interface SessionData {
  userGoal: string | null;
  attachedDocuments: AttachedDocument[];
  intakeSlots: Record<string, string>;
  pipelineName: string | null;
  planLabels: (string | null)[];
}

const EMPTY_DATA: SessionData = {
  userGoal: null,
  attachedDocuments: [],
  intakeSlots: {},
  pipelineName: null,
  planLabels: [],
};

const cache = new Map<number, SessionData>();

export function useSmartRouterSessionContext(): SmartRouterSessionContext {
  const ctx = useOrchestratorContext();
  const sessionId = ctx.inRouter ? ctx.sessionId : null;
  const [data, setData] = useState<SessionData | null>(() =>
    sessionId != null && cache.has(sessionId) ? cache.get(sessionId)! : null,
  );
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef<number | null>(null);

  useEffect(() => {
    if (sessionId == null) {
      setData(null);
      return;
    }
    if (cache.has(sessionId)) {
      setData(cache.get(sessionId)!);
      return;
    }
    if (fetchedRef.current === sessionId) return;
    fetchedRef.current = sessionId;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const { session } = await getSession(sessionId);
        if (cancelled) return;

        const docs = Array.isArray(session.metadata?.attached_documents)
          ? (session.metadata!.attached_documents as AttachedDocument[])
          : [];
        const plan = Array.isArray((session as any).navigation_plan)
          ? ((session as any).navigation_plan as Array<{ label?: string }>)
          : [];

        const resolved: SessionData = {
          userGoal: session.user_goal || null,
          attachedDocuments: docs,
          intakeSlots: (session as any).intake_slots || {},
          pipelineName: session.pipeline_snapshot?.name || null,
          planLabels: plan.map((s) => s?.label || null),
        };
        cache.set(sessionId, resolved);
        setData(resolved);
      } catch {
        if (cancelled) return;
        fetchedRef.current = null;
        setData(EMPTY_DATA);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (sessionId == null) {
    return {
      inRouter: false,
      sessionId: null,
      loading: false,
      userGoal: null,
      attachedDocuments: [],
      intakeSlots: {},
      pipelineName: null,
      stepLabel: null,
      stepNumber: null,
      stepTotal: null,
    };
  }

  const d = data || EMPTY_DATA;
  const idx = ctx.stepIndex;
  return {
    inRouter: true,
    sessionId,
    loading,
    userGoal: d.userGoal,
    attachedDocuments: d.attachedDocuments,
    intakeSlots: d.intakeSlots,
    pipelineName: d.pipelineName,
    stepLabel: idx != null ? d.planLabels[idx] ?? null : null,
    stepNumber: idx != null ? idx + 1 : null,
    stepTotal: d.planLabels.length || null,
  };
}
